import React, { useRef } from 'react';
import { X, Download, Image, Target, TrendingUp, Shield, DollarSign, CheckCircle, AlertTriangle, Layers, Clock, Award, Users } from 'lucide-react';
import { toPng } from 'html-to-image';
import { jsPDF } from 'jspdf';
import logo from '../../assets/icon.png';

const riskColor = (level) => {
  if (!level) return 'bg-gray-100 text-gray-600';
  const l = level.toLowerCase();
  if (l.includes('ridicat') || l.includes('high')) return 'bg-red-100 text-red-700 border-red-200';
  if (l.includes('mediu') || l.includes('medium')) return 'bg-amber-100 text-amber-700 border-amber-200';
  return 'bg-green-100 text-green-700 border-green-200';
};

const statusColor = (status) => {
  if (!status) return 'bg-gray-100 text-gray-600';
  const s = status.toLowerCase();
  if (s.includes('finalizat') || s.includes('atribuit') || s.includes('completat')) return 'bg-green-100 text-green-700';
  if (s.includes('curs') || s.includes('licitatie') || s.includes('activ')) return 'bg-blue-100 text-blue-700';
  return 'bg-amber-100 text-amber-700';
};

const phaseColors = ['#6366f1', '#8b5cf6', '#06b6d4', '#10b981', '#f59e0b', '#ef4444', '#ec4899', '#14b8a6'];

const ScoreBar = ({ label, value, max = 10, color = 'indigo' }) => (
  <div className="flex items-center gap-2">
    <span className="text-xs font-medium text-gray-600 w-28">{label}</span>
    <div className="flex-1 bg-gray-200 rounded-full h-3.5 overflow-hidden">
      <div className={`h-3.5 rounded-full bg-gradient-to-r from-${color}-400 to-${color}-600 transition-all`} style={{ width: `${((value || 0) / max) * 100}%` }} />
    </div>
    <span className={`text-xs font-bold text-${color}-700 w-8 text-right`}>{value || 0}/{max}</span>
  </div>
);

const Section = ({ title, icon: Icon, children, className = '' }) => (
  <div className={`mb-6 ${className}`}>
    <div className="flex items-center gap-2 mb-3 pb-2 border-b-2 border-indigo-100">
      <div className="w-8 h-8 rounded-lg bg-indigo-100 flex items-center justify-center">
        {Icon && <Icon size={16} className="text-indigo-600" />}
      </div>
      <h3 className="text-sm font-bold text-gray-800 uppercase tracking-wide">{title}</h3>
    </div>
    {children}
  </div>
);

const ProjectAIReportModal = ({ isOpen, onClose, report, projectTitle }) => {
  const contentRef = useRef(null);

  if (!isOpen || !report) return null;

  const { project_overview, pipeline_analysis, competitive_analysis, financial_analysis, opportunity_assessment, project_timeline, risk_analysis, strategic_recommendations, complexity_score, wbs } = report;
  const displayTitle = report?.project_title || projectTitle || 'proiect';

  const handleDownloadPDF = async () => {
    if (!contentRef.current) return;
    try {
      const dataUrl = await toPng(contentRef.current, { quality: 0.95, pixelRatio: 2, backgroundColor: '#ffffff' });
      const img = new window.Image();
      img.src = dataUrl;
      img.onload = () => {
        const pdf = new jsPDF('p', 'mm', 'a4');
        const pdfW = pdf.internal.pageSize.getWidth() - 16;
        const pdfH = (img.height * pdfW) / img.width;
        const pageH = pdf.internal.pageSize.getHeight() - 16;
        let y = 0;
        while (y < pdfH) {
          if (y > 0) pdf.addPage();
          pdf.addImage(dataUrl, 'PNG', 8, 8 - y, pdfW, pdfH);
          y += pageH;
        }
        const safeName = displayTitle.replace(/[^a-zA-Z0-9]/g, '_').substring(0, 40);
        pdf.save(`Raport_AI_Proiect_${safeName}.pdf`);
      };
    } catch (e) { console.error('PDF export error:', e); }
  };

  const handleDownloadImage = async () => {
    if (!contentRef.current) return;
    try {
      const dataUrl = await toPng(contentRef.current, { quality: 0.95, pixelRatio: 2, backgroundColor: '#ffffff' });
      const link = document.createElement('a');
      const safeName = displayTitle.replace(/[^a-zA-Z0-9]/g, '_').substring(0, 40);
      link.download = `Raport_AI_Proiect_${safeName}.png`;
      link.href = dataUrl;
      link.click();
    } catch (e) { console.error('Image export error:', e); }
  };

  // Gantt data from timeline
  const ganttData = (project_timeline || []).filter(p => p.start_date && p.end_date);
  const allDates = ganttData.flatMap(p => [new Date(p.start_date), new Date(p.end_date)]).filter(d => !isNaN(d));
  const ganttMinDate = allDates.length > 0 ? new Date(Math.min(...allDates)) : null;
  const ganttMaxDate = allDates.length > 0 ? new Date(Math.max(...allDates)) : null;
  const ganttTotalDays = ganttMinDate && ganttMaxDate ? Math.max(1, (ganttMaxDate - ganttMinDate) / (1000 * 60 * 60 * 24)) : 1;

  return (
    <div className="fixed inset-0 z-50 flex items-start justify-center bg-black/60 overflow-y-auto py-4">
      <div className="bg-white rounded-2xl shadow-2xl w-full max-w-4xl mx-4 relative">
        {/* Toolbar */}
        <div className="sticky top-0 z-10 bg-white border-b border-gray-200 px-6 py-3 rounded-t-2xl flex items-center justify-between">
          <div className="flex items-center gap-2">
            <img src={logo} alt="Licitatii-Constructii" className="w-6 h-6" />
            <span className="text-sm font-bold text-gray-700">Raport AI Proiect</span>
          </div>
          <div className="flex items-center gap-1">
            <button onClick={handleDownloadPDF} className="flex items-center gap-1 px-3 py-1.5 text-xs font-semibold text-indigo-700 bg-indigo-50 hover:bg-indigo-100 rounded-lg transition" title="Descarca PDF">
              <Download size={14} /> PDF
            </button>
            <button onClick={handleDownloadImage} className="flex items-center gap-1 px-3 py-1.5 text-xs font-semibold text-emerald-700 bg-emerald-50 hover:bg-emerald-100 rounded-lg transition" title="Descarca imagine">
              <Image size={14} /> PNG
            </button>
            <button onClick={onClose} className="p-2 hover:bg-gray-100 rounded-lg transition"><X size={16} /></button>
          </div>
        </div>

        {/* Content */}
        <div ref={contentRef} className="p-6 bg-white">

          {/* Header — Branding + Titlu Proiect */}
          <div className="bg-gradient-to-r from-indigo-600 via-indigo-700 to-purple-700 rounded-xl p-6 mb-6 text-white">
            <div className="flex items-center gap-3 mb-3">
              <img src={logo} alt="Licitatii-Constructii" className="w-10 h-10 rounded-lg bg-white/20 p-1" />
              <div>
                <p className="text-xs text-indigo-200 font-semibold tracking-wider uppercase">Licitatii-Constructii - Raport AI</p>
                <p className="text-[10px] text-indigo-300">Generat automat cu inteligenta artificiala</p>
              </div>
            </div>
            <h1 className="text-xl font-extrabold leading-tight mb-2">{displayTitle !== 'proiect' ? displayTitle : project_overview?.summary?.split('.')[0]}</h1>
            <div className="flex flex-wrap gap-2 mt-2">
              {project_overview?.delivery_model && (
                <span className="text-xs bg-white/20 px-2.5 py-1 rounded-full font-semibold">{project_overview.delivery_model}</span>
              )}
              {project_overview?.project_type && (
                <span className="text-xs bg-white/20 px-2.5 py-1 rounded-full font-semibold">{project_overview.project_type}</span>
              )}
              {project_overview?.building_type && (
                <span className="text-xs bg-white/20 px-2.5 py-1 rounded-full font-semibold">{project_overview.building_type}</span>
              )}
              {pipeline_analysis?.current_status && (
                <span className="text-xs bg-white/30 px-2.5 py-1 rounded-full font-bold">{pipeline_analysis.current_status}</span>
              )}
            </div>
          </div>

          {/* Viziune de ansamblu */}
          {project_overview && (
            <Section title="Viziune de Ansamblu" icon={Target}>
              <p className="text-sm text-gray-700 leading-relaxed mb-3">{project_overview.summary}</p>
              {project_overview.scope_description && project_overview.scope_description !== project_overview.summary && (
                <p className="text-xs text-gray-600 bg-gray-50 p-3 rounded-lg mb-3">{project_overview.scope_description}</p>
              )}
              {project_overview.delivery_model_justification && (
                <div className="bg-indigo-50 rounded-lg p-3 border border-indigo-100 mb-3">
                  <p className="text-xs font-semibold text-indigo-700 mb-1">Model de executie: {project_overview.delivery_model}</p>
                  <p className="text-xs text-indigo-600">{project_overview.delivery_model_justification}</p>
                </div>
              )}
              {project_overview.key_features?.length > 0 && (
                <div className="flex flex-wrap gap-1.5">
                  {project_overview.key_features.map((f, i) => (
                    <span key={i} className="text-xs bg-indigo-100 text-indigo-700 px-2.5 py-1 rounded-full font-medium">{f}</span>
                  ))}
                </div>
              )}
            </Section>
          )}

          {/* Pipeline */}
          {pipeline_analysis && (
            <Section title="Analiza Pipeline" icon={TrendingUp}>
              <div className="flex items-center gap-2 mb-3">
                <span className={`text-sm font-bold px-3 py-1.5 rounded-full border ${statusColor(pipeline_analysis.current_status)}`}>{pipeline_analysis.current_status}</span>
                {pipeline_analysis.delay_risk && (
                  <span className={`text-xs font-semibold px-2.5 py-1 rounded-full border ${riskColor(pipeline_analysis.delay_risk)}`}>Risc intarziere: {pipeline_analysis.delay_risk}</span>
                )}
              </div>
              <p className="text-sm text-gray-700 mb-4">{pipeline_analysis.status_description}</p>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
                {pipeline_analysis.completed_phases?.length > 0 && (
                  <div className="bg-green-50 rounded-xl p-4 border border-green-200">
                    <p className="text-xs font-bold text-green-700 mb-2 flex items-center gap-1"><CheckCircle size={12} /> Completate</p>
                    {pipeline_analysis.completed_phases.map((p, i) => (
                      <div key={i} className="text-xs text-green-800 mb-1.5 pl-4 border-l-2 border-green-300">{p.phase}{p.value > 0 && ` — ${p.value?.toLocaleString('ro-RO')} ${p.currency || 'RON'}`}</div>
                    ))}
                  </div>
                )}
                {pipeline_analysis.active_phases?.length > 0 && (
                  <div className="bg-blue-50 rounded-xl p-4 border border-blue-200">
                    <p className="text-xs font-bold text-blue-700 mb-2 flex items-center gap-1"><Clock size={12} /> Active</p>
                    {pipeline_analysis.active_phases.map((p, i) => (
                      <div key={i} className="text-xs text-blue-800 mb-1.5 pl-4 border-l-2 border-blue-300">{p.phase}: {p.description}{p.value > 0 && ` — ${p.value?.toLocaleString('ro-RO')} ${p.currency || 'RON'}`}</div>
                    ))}
                  </div>
                )}
                {pipeline_analysis.missing_phases?.length > 0 && (
                  <div className="bg-amber-50 rounded-xl p-4 border border-amber-200">
                    <p className="text-xs font-bold text-amber-700 mb-2 flex items-center gap-1"><AlertTriangle size={12} /> Lipsa</p>
                    {pipeline_analysis.missing_phases.map((p, i) => (
                      <div key={i} className="text-xs text-amber-800 mb-1.5 pl-4 border-l-2 border-amber-300">{p.phase}: {p.description}</div>
                    ))}
                  </div>
                )}
              </div>
              {pipeline_analysis.next_expected_action && (
                <div className="mt-3 bg-emerald-50 rounded-lg p-3 border border-emerald-200">
                  <p className="text-xs font-bold text-emerald-700">Urmatorul pas estimat:</p>
                  <p className="text-xs text-emerald-600">{pipeline_analysis.next_expected_action}</p>
                </div>
              )}
            </Section>
          )}

          {/* Gantt Chart */}
          {ganttData.length > 0 && ganttMinDate && (
            <Section title="Gantt - Timeline Proiect" icon={Clock}>
              <div className="bg-gray-50 rounded-xl p-4 border border-gray-200 overflow-x-auto">
                <div className="min-w-[500px]">
                  {/* Header */}
                  <div className="flex items-center text-[10px] text-gray-500 mb-2 pl-36">
                    <span>{ganttMinDate.toLocaleDateString('ro-RO', { month: 'short', year: 'numeric' })}</span>
                    <span className="ml-auto">{ganttMaxDate.toLocaleDateString('ro-RO', { month: 'short', year: 'numeric' })}</span>
                  </div>
                  {/* Bars */}
                  {ganttData.map((phase, i) => {
                    const start = new Date(phase.start_date);
                    const end = new Date(phase.end_date);
                    const leftPct = ((start - ganttMinDate) / (1000 * 60 * 60 * 24)) / ganttTotalDays * 100;
                    const widthPct = Math.max(3, ((end - start) / (1000 * 60 * 60 * 24)) / ganttTotalDays * 100);
                    const color = phaseColors[i % phaseColors.length];
                    return (
                      <div key={i} className="flex items-center mb-1.5 gap-2">
                        <div className="w-32 text-xs font-medium text-gray-700 truncate shrink-0" title={phase.phase}>{phase.phase}</div>
                        <div className="flex-1 bg-gray-200 rounded-full h-5 relative">
                          <div className="absolute h-5 rounded-full flex items-center px-2 text-[9px] font-bold text-white overflow-hidden"
                            style={{ left: `${leftPct}%`, width: `${widthPct}%`, backgroundColor: color, minWidth: '20px' }}>
                            {phase.duration_months && `${phase.duration_months}l`}
                          </div>
                        </div>
                        <span className={`text-[10px] px-1.5 py-0.5 rounded font-semibold shrink-0 ${statusColor(phase.status)}`}>{phase.status}</span>
                      </div>
                    );
                  })}
                </div>
              </div>
            </Section>
          )}

          {/* Analiza Competitiva */}
          {competitive_analysis?.has_awards && competitive_analysis.winners?.length > 0 && (
            <Section title="Analiza Competitiva - Castigatori" icon={Award}>
              <div className="overflow-x-auto rounded-lg border border-green-200">
                <table className="w-full text-xs">
                  <thead><tr className="bg-green-50">
                    <th className="text-left p-2.5 font-bold text-green-800">Castigator</th>
                    <th className="text-left p-2.5 font-bold text-green-800">CUI</th>
                    <th className="text-right p-2.5 font-bold text-green-800">Contract</th>
                    <th className="text-right p-2.5 font-bold text-green-800">Estimat</th>
                    <th className="text-right p-2.5 font-bold text-green-800">Discount</th>
                    <th className="text-left p-2.5 font-bold text-green-800">Faza</th>
                  </tr></thead>
                  <tbody>
                    {competitive_analysis.winners.map((w, i) => (
                      <tr key={i} className="border-t border-green-100 hover:bg-green-50/50">
                        <td className="p-2.5 font-semibold text-gray-800">{w.company_name}</td>
                        <td className="p-2.5 text-gray-600">{w.cui}</td>
                        <td className="p-2.5 text-right font-bold text-green-700">{w.contract_value?.toLocaleString('ro-RO')}</td>
                        <td className="p-2.5 text-right text-gray-500">{w.estimated_value?.toLocaleString('ro-RO')}</td>
                        <td className="p-2.5 text-right"><span className={`font-bold ${(w.discount_percent || 0) > 0 ? 'text-green-600' : 'text-gray-400'}`}>{w.discount_percent ? `-${w.discount_percent}%` : '-'}</span></td>
                        <td className="p-2.5 text-gray-600">{w.phase}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
              {competitive_analysis.market_insights && (
                <p className="text-xs text-gray-600 mt-2 italic bg-gray-50 p-2 rounded">{competitive_analysis.market_insights}</p>
              )}
            </Section>
          )}

          {/* Analiza Financiara */}
          {financial_analysis && (
            <Section title="Analiza Financiara" icon={DollarSign}>
              <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-4">
                <div className="bg-gradient-to-br from-green-50 to-white rounded-xl p-3 border border-green-200 text-center">
                  <p className="text-[10px] text-gray-500 font-semibold">Valoare estimata</p>
                  <p className="text-base font-black text-green-700">{financial_analysis.total_estimated_value?.toLocaleString('ro-RO')}</p>
                  <p className="text-[10px] text-gray-400">{financial_analysis.currency || 'RON'}</p>
                </div>
                {financial_analysis.total_contracted_value > 0 && (
                  <div className="bg-gradient-to-br from-blue-50 to-white rounded-xl p-3 border border-blue-200 text-center">
                    <p className="text-[10px] text-gray-500 font-semibold">Valoare contractata</p>
                    <p className="text-base font-black text-blue-700">{financial_analysis.total_contracted_value?.toLocaleString('ro-RO')}</p>
                    <p className="text-[10px] text-gray-400">{financial_analysis.currency || 'RON'}</p>
                  </div>
                )}
                {financial_analysis.funding_source && (
                  <div className="bg-gradient-to-br from-teal-50 to-white rounded-xl p-3 border border-teal-200 text-center">
                    <p className="text-[10px] text-gray-500 font-semibold">Sursa finantare</p>
                    <p className="text-sm font-bold text-teal-700">{financial_analysis.funding_source}</p>
                  </div>
                )}
                {financial_analysis.budget_utilization_percent > 0 && (
                  <div className="bg-gradient-to-br from-purple-50 to-white rounded-xl p-3 border border-purple-200 text-center">
                    <p className="text-[10px] text-gray-500 font-semibold">Utilizare buget</p>
                    <p className="text-base font-black text-purple-700">{financial_analysis.budget_utilization_percent}%</p>
                  </div>
                )}
              </div>
              {financial_analysis.budget_breakdown?.length > 0 && (
                <div className="space-y-1.5">
                  {financial_analysis.budget_breakdown.map((b, i) => (
                    <div key={i} className="flex items-center gap-2">
                      <span className="text-xs text-gray-600 w-40 shrink-0">{b.category}</span>
                      <div className="flex-1 bg-gray-200 rounded-full h-5 overflow-hidden">
                        <div className="h-5 rounded-full flex items-center justify-end pr-2 text-[10px] text-white font-bold"
                          style={{ width: `${Math.min(b.percentage || 0, 100)}%`, backgroundColor: phaseColors[i % phaseColors.length] }}>
                          {b.percentage}%
                        </div>
                      </div>
                      {b.value > 0 && <span className="text-xs text-gray-500 w-24 text-right shrink-0">{b.value?.toLocaleString('ro-RO')}</span>}
                    </div>
                  ))}
                </div>
              )}
              {financial_analysis.funding_risk && (
                <div className="mt-3 bg-amber-50 rounded-lg p-3 border border-amber-200">
                  <p className="text-xs text-amber-800"><AlertTriangle size={11} className="inline mr-1" /><span className="font-bold">Risc finantare:</span> {financial_analysis.funding_risk}</p>
                </div>
              )}
            </Section>
          )}

          {/* Oportunitate + Complexitate — side by side */}
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-6">
            {opportunity_assessment && (
              <div>
                <Section title="Evaluare Oportunitate" icon={Target} className="mb-0">
                  {opportunity_assessment.attractiveness_score && (
                    <div className="flex items-center gap-4 mb-4">
                      <div className={`w-16 h-16 rounded-2xl flex items-center justify-center text-2xl font-black text-white shadow-lg ${
                        opportunity_assessment.attractiveness_score >= 7 ? 'bg-gradient-to-br from-green-400 to-green-600' :
                        opportunity_assessment.attractiveness_score >= 4 ? 'bg-gradient-to-br from-amber-400 to-amber-600' : 'bg-gradient-to-br from-red-400 to-red-600'
                      }`}>{opportunity_assessment.attractiveness_score}</div>
                      <div className="flex-1">
                        <p className="text-xs font-bold text-gray-800">Scor atractivitate: {opportunity_assessment.attractiveness_score}/10</p>
                        <p className="text-xs text-gray-600 leading-snug">{opportunity_assessment.attractiveness_justification}</p>
                      </div>
                    </div>
                  )}
                  {opportunity_assessment.required_certifications?.length > 0 && (
                    <div className="mb-2">
                      <p className="text-[10px] font-bold text-gray-500 mb-1"><Shield size={10} className="inline mr-1" />CERTIFICARI NECESARE</p>
                      <div className="flex flex-wrap gap-1">{opportunity_assessment.required_certifications.map((c, i) => (
                        <span key={i} className="text-[10px] bg-purple-100 text-purple-700 px-2 py-0.5 rounded-full font-semibold">{c}</span>
                      ))}</div>
                    </div>
                  )}
                  {opportunity_assessment.key_personnel?.length > 0 && (
                    <div className="mb-2">
                      <p className="text-[10px] font-bold text-gray-500 mb-1"><Users size={10} className="inline mr-1" />PERSONAL CHEIE</p>
                      <div className="flex flex-wrap gap-1">{opportunity_assessment.key_personnel.map((p, i) => (
                        <span key={i} className="text-[10px] bg-blue-100 text-blue-700 px-2 py-0.5 rounded-full font-semibold">{p}</span>
                      ))}</div>
                    </div>
                  )}
                  {opportunity_assessment.participation_checklist?.length > 0 && (
                    <div className="bg-indigo-50 rounded-lg p-3 border border-indigo-100 mt-2">
                      <p className="text-[10px] font-bold text-indigo-700 mb-1.5">CHECKLIST PARTICIPARE</p>
                      {opportunity_assessment.participation_checklist.map((item, i) => (
                        <p key={i} className="text-[10px] text-indigo-800 mb-0.5 flex items-start gap-1"><CheckCircle size={10} className="text-indigo-500 shrink-0 mt-0.5" />{item}</p>
                      ))}
                    </div>
                  )}
                </Section>
              </div>
            )}

            {complexity_score && (
              <div>
                <Section title="Complexitate Proiect" icon={Layers} className="mb-0">
                  <div className="space-y-2.5 mb-3">
                    <ScoreBar label="General" value={complexity_score.overall} color="indigo" />
                    <ScoreBar label="Tehnic" value={complexity_score.technical} color="blue" />
                    <ScoreBar label="Financiar" value={complexity_score.financial} color="green" />
                    <ScoreBar label="Organizational" value={complexity_score.organizational} color="purple" />
                    <ScoreBar label="Reglementare" value={complexity_score.regulatory} color="orange" />
                  </div>
                  {complexity_score.justification && (
                    <p className="text-xs text-gray-600 bg-gray-50 p-2 rounded italic">{complexity_score.justification}</p>
                  )}
                </Section>
              </div>
            )}
          </div>

          {/* Riscuri */}
          {risk_analysis?.risks?.length > 0 && (
            <Section title="Analiza Riscuri" icon={AlertTriangle}>
              <span className={`text-xs font-bold px-3 py-1.5 rounded-full border inline-block mb-3 ${riskColor(risk_analysis.overall_risk_level)}`}>Nivel risc global: {risk_analysis.overall_risk_level}</span>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
                {risk_analysis.risks.map((risk, i) => (
                  <div key={i} className="bg-gray-50 rounded-lg p-3 border border-gray-200">
                    <div className="flex items-center gap-1.5 mb-1">
                      <span className="text-xs font-bold text-gray-800">{risk.category}</span>
                      <span className={`text-[9px] px-1.5 py-0.5 rounded-full font-semibold border ${riskColor(risk.probability)}`}>P:{risk.probability}</span>
                      <span className={`text-[9px] px-1.5 py-0.5 rounded-full font-semibold border ${riskColor(risk.impact)}`}>I:{risk.impact}</span>
                    </div>
                    <p className="text-xs text-gray-700 mb-1">{risk.description}</p>
                    {risk.mitigation && <p className="text-[10px] text-green-700 bg-green-50 rounded px-2 py-1"><Shield size={9} className="inline mr-0.5" />{risk.mitigation}</p>}
                  </div>
                ))}
              </div>
            </Section>
          )}

          {/* Recomandari */}
          {strategic_recommendations?.length > 0 && (
            <Section title="Recomandari Strategice" icon={Target}>
              <div className="space-y-2">
                {strategic_recommendations.map((rec, i) => (
                  <div key={i} className="flex gap-3 items-start bg-gradient-to-r from-purple-50 to-white rounded-lg p-3 border border-purple-200">
                    <div className="w-7 h-7 rounded-full bg-purple-100 text-purple-700 flex items-center justify-center font-bold text-xs shrink-0">{i + 1}</div>
                    <div className="flex-1">
                      <div className="flex items-center gap-2 mb-0.5">
                        <span className="text-xs font-bold text-purple-800">{rec.title}</span>
                        {rec.priority && <span className={`text-[9px] px-1.5 py-0.5 rounded-full font-semibold ${rec.priority?.toLowerCase().includes('ridicat') ? 'bg-red-100 text-red-700' : rec.priority?.toLowerCase().includes('medie') ? 'bg-amber-100 text-amber-700' : 'bg-blue-100 text-blue-700'}`}>{rec.priority}</span>}
                      </div>
                      <p className="text-xs text-gray-700">{rec.description}</p>
                      {rec.target_audience && <p className="text-[10px] text-gray-500 mt-0.5">Pentru: {rec.target_audience}</p>}
                    </div>
                  </div>
                ))}
              </div>
            </Section>
          )}

          {/* WBS */}
          {wbs?.length > 0 && (
            <Section title="WBS - Structura Proiect" icon={Layers}>
              <div className="bg-gray-50 rounded-xl p-4 border border-gray-200">
                {wbs.map((item, i) => (
                  <div key={i} className={`flex items-center gap-2 py-1 ${item.level === 1 ? 'font-bold text-sm text-gray-900 mt-1' : item.level === 2 ? 'pl-6 text-xs text-gray-700' : 'pl-12 text-xs text-gray-500'}`}>
                    <span className="text-indigo-500 font-mono text-[11px] w-12 shrink-0">{item.code}</span>
                    <span className={item.level === 1 ? 'border-b border-indigo-200 pb-0.5 flex-1' : ''}>{item.description}</span>
                  </div>
                ))}
              </div>
            </Section>
          )}

          {/* Footer */}
          <div className="mt-6 pt-4 border-t border-gray-200 flex items-center justify-between">
            <div className="flex items-center gap-2">
              <img src={logo} alt="Licitatii-Constructii" className="w-5 h-5 opacity-50" />
              <span className="text-[10px] text-gray-400">Licitatii-Constructii - Raport generat cu AI</span>
            </div>
            <span className="text-[10px] text-gray-400">{new Date().toLocaleDateString('ro-RO', { day: 'numeric', month: 'long', year: 'numeric' })}</span>
          </div>

        </div>
      </div>
    </div>
  );
};

export default ProjectAIReportModal;
